import {
  batchCreateEvent,
  batchDeleteElementAndSubs,
  getNextUuid,
  getNestedSubelementsByType,
  createStandardizedFieldValue,
  createFieldValue,
  batchEditEventType,
  batchCreateEventType,
  batchCreateBasicField,
  getSlsTypeByValue,
  getMostRecentFieldValue,
  batchCreateIntermediarySuperForField,
  INDEXED_INCORRECTLY,
  batchCreateRelationship,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import isEmpty from 'lodash.isempty'
import { getRelationshipsBetweenTwoPeopleByType } from './temporary.utils'
import { replaceRelationshipPerson } from './relationship.utils'

const ERR_PERSON_ID = 'Primary person must have an ID'

/**
 * Returns an attribution object for event changes.
 * @param {string} appName - The name of the application making the change.
 * @returns {{comment: string, reasonId: string, app: string}} Attribution object.
 */
function getAttribution(appName) {
  return { comment: '', reasonId: INDEXED_INCORRECTLY, app: appName }
}

/**
 * Validates the inputs for dispatching event changes.
 * @param {Object} person - The person object, must have an id.
 * @param {Array} events - The array of event objects.
 * @throws {Error} If person or person.id is missing.
 * @returns {boolean} True if inputs are valid, false otherwise.
 */
function validateDispatchInputs(person, events) {
  if (!person?.id) throw new Error(ERR_PERSON_ID)
  return Array.isArray(events)
}

/**
 * Retrieve a text value from suggest fields or text input fields.
 * @param {Object} formValue - The form value object with originalText or primaryText and resources if applicable
 * @returns {String} A text value
 */
function getMixedFieldValue(formValue) {
  return (
    formValue?.primaryText ||
    formValue?.originalText ||
    formValue?.standard?.standardizedText ||
    formValue?.standard?.originalText
  )
}

/**
 * Prepares a field value object for dispatch.
 * @param {Object} fieldObj - The field object containing value and type.
 * @param {string} appName - The name of the application.
 * @returns {Object} The prepared field value.
 */
function prepareFieldValue(fieldObj, appName) {
  let fieldValue = {}

  if (fieldObj?.formValue?.standard) {
    fieldValue = createStandardizedFieldValue(fieldObj.formValue, fieldObj.fieldType, getAttribution(appName))
  } else {
    const newValue = getMixedFieldValue(fieldObj.formValue)
    fieldValue = createFieldValue(newValue, getAttribution(appName))
  }

  return fieldValue
}

/**
 * Gets the field value for an event type.
 * @param {Object} event - The event object.
 * @param {Array} slsTypes - The list of SLS types.
 * @param {string} appName - The name of the application.
 * @returns {Object} The event type field value.
 */
function getEventTypeFieldValue(event, slsTypes, appName) {
  const eventType = getSlsTypeByValue(event.eventType?.key, slsTypes)
  const eventTypeLabel = eventType?.displayLabel
  return createFieldValue(eventTypeLabel, getAttribution(appName), eventType?.uri)
}

/**
 * Finds the element ID from a list of dispatches for a given element type.
 * @param {Array} dispatches - The list of dispatch actions.
 * @param {string} elementType - The element type to search for.
 * @returns {string|undefined} The found element ID, or undefined.
 */
function findElementIdFromCreate(dispatches, elementType) {
  return dispatches?.find((action) => action.type === 'CREATE_ELEMENT' && action.value.elementType === elementType)
    ?.value.id
}

export function handleExistingEventField(fieldObj, event, defaultValues, person, appName) {
  if (!fieldObj) return []
  const dispatches = []
  const fieldTypeField = getNestedSubelementsByType(defaultValues, fieldObj.fieldType, true)[0]
  const originalFieldValue = getMostRecentFieldValue(fieldTypeField)
  const newFieldValueCandidate = getMixedFieldValue(fieldObj.formValue)
  if (
    originalFieldValue === newFieldValueCandidate &&
    fieldTypeField?.formValue?.standard === fieldObj?.formValue?.standard
  )
    return []

  const fieldValue = prepareFieldValue(fieldObj, appName)

  if (!fieldObj.personId) {
    // This field wasn't read from sls, so we need to create it
    const existingIntermediary = fieldObj.fieldType === 'DATE' ? event.dates[0] : event.places[0]
    const intermediaryId = existingIntermediary?.id || getNextUuid()
    if (!existingIntermediary?.id) {
      const newFieldTypeDispatches = batchCreateIntermediarySuperForField({
        partitionKey: defaultValues?.partitionKey,
        intermediaryType: fieldObj?.fieldType,
        intermediaryId,
        superId: event.id,
        superType: 'EVENT',
      })

      dispatches.push(...newFieldTypeDispatches)
    }

    const newField = batchCreateBasicField({
      partitionKey: person.partitionKey,
      superId: intermediaryId,
      fieldType: fieldObj.fieldType,
      fieldValue,
      id: fieldObj.id,
      superElementType: fieldObj.fieldType,
    })
    dispatches.push(...newField)
  } else if (fieldValue) {
    dispatches.push({
      type: 'ADD_FV',
      value: { partitionKey: defaultValues?.partitionKey, id: fieldObj.id, fieldValue },
    })
  }
  return dispatches
}

/**
 * Handles updates to existing events and returns dispatch actions.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.defaultValues - The default values for the event.
 * @param {Object} params.person - The person object.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {string} params.appName - The application name.
 * @returns {Array} List of dispatch actions for existing events.
 */
export function handleExistingEvents({ event, defaultValues, person, slsTypes, dirtyFields, appName }) {
  const dispatches = []
  if (!event || !person.partitionKey || !event?.id) return dispatches
  if (
    dirtyFields &&
    typeof dirtyFields === 'object' &&
    !Array.isArray(dirtyFields) &&
    Object.keys(dirtyFields).length > 0
  ) {
    Object.keys(dirtyFields).forEach((field) => {
      if (field === 'eventType') {
        const eventTypeField = getNestedSubelementsByType(defaultValues, 'EVENT_TYPE')[0]
        const typeFieldValue = getEventTypeFieldValue(event, slsTypes, appName)
        const hasEventTypeField = !isEmpty(eventTypeField)
        const existingEventType = getMostRecentFieldValue(eventTypeField)
        const newEventType = event.eventType?.primaryText
        if (hasEventTypeField && existingEventType !== newEventType) {
          dispatches.push(
            ...batchEditEventType({
              partitionKey: defaultValues?.partitionKey,
              typeFieldValue,
              typeFieldId: eventTypeField.id,
            })
          )
        } else if (!hasEventTypeField) {
          dispatches.push(
            ...batchCreateEventType({
              partitionKey: defaultValues?.partitionKey,
              typeFieldValue,
              typeFieldId: getNextUuid(),
              elementId: event.id,
            })
          )
        }
      } else if (field === 'otherPerson') {
        dispatches.push(
          ...replaceRelationshipPerson({
            relationship: event?.relationshipData,
            personId: person.id,
            otherPersonId: event?.otherPerson?.key,
          })
        )
      } else {
        event[field]?.[0]?.subElements?.forEach?.((fieldObj) => {
          dispatches.push(...handleExistingEventField(fieldObj, event, defaultValues, person, appName))
        })
      }
    })
  }
  return dispatches
}

/**
 * Attach existing event to current person or to all primary persons if the event is primary.
 * Then submit field updates.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object from SLS.
 * @param {Object} params.person - The person object from SLS.
 * @param {Array} params.images - The images from SLS.
 * @param {Object} params.record - The record object from SLS.
 * @param {Array} params.primaryPersons - The list of images from SLS state.
 * @param {Object} params.dirtyFields - The dirty fields object from formState for this event.
 * @param {string} params.appName - The application name.
 * @returns {Array} dispatches
 */
function attachExistingEvent({ event, person, images, record, primaryPersons, dirtyFields, appName }) {
  const dispatches = []
  if (event.primary && record) {
    // Make sure this event is linked to all primary persons in this record, including current person if they are primary.
    primaryPersons?.forEach((primaryPerson) => {
      if (!event.superElements?.some((supEl) => supEl.id === primaryPerson.id)) {
        dispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: primaryPerson.partitionKey,
            subPartitionKey: primaryPerson.partitionKey, // Events are in same partition as person
            superId: primaryPerson.id,
            subId: event.existingEventId,
            elementType: 'PERSON',
          },
        })
      }
    })
  } else {
    // Link the person to the existing event
    dispatches.push({
      type: 'ADD_SUB',
      value: {
        superPartitionKey: person.partitionKey,
        subPartitionKey: person.partitionKey, // Events are in same partition as person
        superId: person.id,
        subId: event.existingEventId,
        elementType: 'PERSON',
      },
    })
  }

  // Update field values for the existing event.
  if (dirtyFields?.dates) {
    event.dates?.[0]?.subElements?.forEach?.((dateFieldObj) => {
      dispatches.push(
        ...handleExistingEventField(
          dateFieldObj,
          event,
          getNestedOrFloatingElementById(images, event.id),
          person,
          appName
        )
      )
    })
  }
  if (dirtyFields?.places) {
    event.places?.[0]?.subElements?.forEach?.((placeFieldObj) => {
      dispatches.push(
        ...handleExistingEventField(
          placeFieldObj,
          event,
          getNestedOrFloatingElementById(images, event.id),
          person,
          appName
        )
      )
    })
  }
  return dispatches
}

/**
 * Creates event and adds it to the person or primaryPersons. If this is a relationship event, this adds the relationship.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.person - The person object.
 * @param {Boolean} params.attachToThisPerson - Whether to attach the event to this person or to all primary persons.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Boolean} params.createDate - Whether to create a DATE element
 * @param {Boolean} params.createPlace - Whether to create a PLACE element
 * @param {string} params.appName - The application name.
 * @returns {Array} List of dispatch actions for new events.
 */
function handleEventAndRelationshipCreation({
  person,
  event,
  attachToThisPerson,
  primaryPersons,
  slsTypes,
  createDate,
  createPlace,
  appName,
}) {
  const newEventId = event.id || getNextUuid()
  const eventType = getSlsTypeByValue(event?.eventType?.key || event?.eventType, slsTypes)
  const eventTypeFieldValue = getEventTypeFieldValue(event, slsTypes, appName)
  let eventParentId = attachToThisPerson ? person.id : null
  const otherPersonId = event?.otherPerson?.key
  const relType = event?.eventType?.relType
  const dispatches = []

  // Creating relationship if applicable
  const isRelationshipEvent = event?.eventType?.isRelationshipEvent
  if (isRelationshipEvent) {
    // Check for existing relationship of the correct type that can be reused to attach the event to
    const possibleExistingRelationship = getRelationshipsBetweenTwoPeopleByType({
      person,
      otherPersonId,
      relType,
    })?.[0]
    const existingEvent = getNestedSubelementsByType(possibleExistingRelationship, 'EVENT')?.[0]

    // If no event found, reuse the relationship, else create a new relationship
    if (possibleExistingRelationship && !existingEvent) {
      eventParentId = possibleExistingRelationship?.id
    } else {
      eventParentId = getNextUuid()
      const relationshipDispatches = batchCreateRelationship({
        id: eventParentId,
        partitionKey: person.partitionKey,
        person1ID: attachToThisPerson ? person?.id : primaryPersons[0]?.id,
        person2ID: otherPersonId,
        relType,
      })

      dispatches.push(...relationshipDispatches)
    }
  }
  dispatches.push(
    ...batchCreateEvent({
      partitionKey: person.partitionKey,
      eventType: eventType?.eventType,
      personId: eventParentId,
      id: newEventId,
      eventTypeFieldValue,
      createDate,
      createPlace,
      eventTypeId: getNextUuid(),
      extraArgs: { primary: event.primary || false },
      personIdAsRelationshipId: isRelationshipEvent,
    })
  )

  if (event.primary && !isRelationshipEvent) {
    primaryPersons?.forEach((primaryPerson) => {
      // Make all primary persons share this event
      if (primaryPerson?.id !== person.id) {
        // Ignore current person since we added them above unless they are not primary
        dispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: primaryPerson.partitionKey,
            subPartitionKey: primaryPerson.partitionKey,
            superId: primaryPerson.id,
            subId: newEventId,
            elementType: 'PERSON',
          },
        })
      }
    })
  }
  return dispatches
}

/**
 * Handles creation of new events and returns dispatch actions.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.person - The person object.
 * @param {Array} params.images - The list of images from SLS state.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object from formState for this event.
 * @param {string} params.appName - The application name.
 * @returns {Array} List of dispatch actions for new events.
 */
export function handleNewEvents({ event, person, images, slsTypes, dirtyFields, appName }) {
  const dispatches = []
  if (!event || !person?.id || !person?.partitionKey || !event?.eventType) {
    return dispatches
  }
  const attachToThisPerson = !event.primary || person.primary // False if event is primary and person is not, meaning we modify primary persons instead
  const record = person.recordId ? getNestedOrFloatingElementById(images, person.recordId) : null
  const primaryPersons = record?.subElements?.filter((se) => se.elementType === 'PERSON' && se.primary)

  if (!attachToThisPerson && !primaryPersons?.length) {
    return dispatches
  }
  // Check if this is linking to an existing event
  if (event.existingEventId) {
    return dispatches.concat(
      attachExistingEvent({ event, person, images, record, primaryPersons, dirtyFields, appName })
    )
  }

  // Determine if we need to create date/place fields based on presence of data
  const createDate = !!getMixedFieldValue(event?.dates?.[0]?.subElements?.[0]?.formValue)
  const createPlace = !!getMixedFieldValue(event?.places?.[0]?.subElements?.[0]?.formValue)

  dispatches.push(
    ...handleEventAndRelationshipCreation({
      person,
      event,
      attachToThisPerson,
      primaryPersons,
      slsTypes,
      createDate,
      createPlace,
      appName,
    })
  )

  const dateId = findElementIdFromCreate(dispatches, 'DATE')
  const placeId = findElementIdFromCreate(dispatches, 'PLACE')

  Object.keys(event).forEach((field) => {
    if (['dates', 'places'].includes(field) && event[field]?.length > 0) {
      const fieldObj = event[field]?.[0]?.subElements?.[0]
      if (!fieldObj) return
      const fieldValue = prepareFieldValue(fieldObj, appName)
      let fieldId = null
      if (fieldObj.fieldType === 'DATE' && createDate) {
        fieldId = dateId
      } else if (fieldObj.fieldType === 'PLACE' && createPlace) {
        fieldId = placeId
      }
      if (fieldId) {
        dispatches.push(
          ...batchCreateBasicField({
            partitionKey: person.partitionKey,
            superId: fieldId,
            fieldType: fieldObj.fieldType,
            fieldValue,
            superElementType: fieldObj.fieldType,
            id: fieldObj.id,
          })
        )
      }
    }
  })

  return dispatches
}

/**
 * Handles deletion of an event and returns dispatch actions.
 * @param {Object} event - The event object to delete.
 * @returns {Array} List of dispatch actions for deleting the event.
 */
export function handleDeletedEvents(event) {
  const dispatches = []

  dispatches.push(
    ...batchDeleteElementAndSubs({
      element: event,
    })
  )
  return dispatches
}

/**
 * Gets dispatch actions for a single event, handling both new and existing events.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.eventsById - Map of event IDs to event data.
 * @param {Array} params.images = The list of images from SLS state.
 * @param {Object} params.person - The person object.
 * @param {string} params.appName - The application name.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {number} params.eventIndex - The index of the event.
 * @returns {Array} List of dispatch actions for the event.
 */
function getEventDispatchesForSingleEvent({
  event,
  eventsById,
  person,
  images,
  appName,
  slsTypes,
  dirtyFields,
  eventIndex,
}) {
  if (!event) return []
  if (eventsById[event.id]) {
    return handleExistingEvents({
      event,
      defaultValues: eventsById[event.id],
      person,
      appName,
      slsTypes,
      dirtyFields: dirtyFields ? dirtyFields[eventIndex] : undefined,
    })
  }
  return handleNewEvents({
    event,
    images,
    person,
    slsTypes,
    dirtyFields: dirtyFields ? dirtyFields[eventIndex] : undefined,
    appName,
  })
}

/**
 * Prepares dispatch actions for a list of events.
 * @param {Object} params - The parameters object.
 * @param {Array} params.events - The list of event objects.
 * @param {Object} params.eventsById - Map of event IDs to event data.
 * @param {Array} params.images = The list of images from SLS state.
 * @param {Object} params.person - The person object.
 * @param {string} params.appName - The application name.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @returns {Array} List of dispatch actions for the events.
 */
function prepareDispatchesForEvents({ events, eventsById, person, images, appName, slsTypes, dirtyFields }) {
  if (!validateDispatchInputs(person, events)) return []
  const dispatches = []

  events.forEach((event, index) => {
    dispatches.push(
      ...getEventDispatchesForSingleEvent({
        event,
        eventsById,
        images,
        person,
        appName,
        slsTypes,
        dirtyFields,
        eventIndex: index,
      })
    )
  })

  return dispatches
}

function getAddLinkDispatch(event, person) {
  if (!person) {
    return null
  }
  return {
    type: 'ADD_SUB',
    value: {
      superPartitionKey: person.partitionKey,
      subPartitionKey: event.partitionKey,
      superId: person.id,
      subId: event.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * Creates a REM_SUB dispatch to unlink a person from an event.
 * @param {Object} event - The event object.
 * @param {Object} person - The person to unlink.
 * @returns {Object} The REM_SUB dispatch action.
 */
function getUnlinkDispatch(event, person) {
  if (!person) {
    return null
  }
  return {
    type: 'REM_SUB',
    value: {
      superPartitionKey: person.partitionKey,
      subPartitionKey: event.partitionKey,
      superId: person.id,
      subId: event.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * Gets all dispatch actions for current events, including creates, updates, and deletes.
 * @param {Object} params - The parameters object.
 * @param {Object} params.currentEvents - The current events, grouped by type.
 * @param {Object} params.eventsById - Map of event IDs to event data.
 * @param {String} params.primaryEventId - Id of primary event of record whether attached to this person or not
 * @param {Array} params.images = The list of images from SLS state.
 * @param {Object} params.person - The person object.
 * @param {string} params.appName - The application name.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {Array} params.confirmedUnlinkList - Event and person pairs to unlink (REM_SUB from person).
 * @param {Array} params.confirmedAddLinkList - Event and person pairs to link (ADD_SUB from person).
 * @returns {Array} List of all dispatch actions for the events.
 */
export default function getEventsDispatches({
  currentEvents,
  eventsById,
  primaryEventId,
  images,
  person,
  appName,
  slsTypes,
  dirtyFields,
  confirmedUnlinkList,
  confirmedAddLinkList,
  highlightDispatches,
}) {
  const dispatches = []
  ;['primary', 'additional'].forEach((type) => {
    const events = currentEvents?.[type]
    if (Array.isArray(events) && events.length > 0) {
      dispatches.push(
        ...prepareDispatchesForEvents({
          events,
          eventsById,
          images,
          person,
          appName,
          slsTypes,
          dirtyFields: dirtyFields?.[type] ? dirtyFields[type] : undefined,
        })
      )
    }
  })

  const setRecordPersonIdsDispatches = []
  if (person.recordId && dispatches.some((disp) => disp.type === 'CREATE_ELEMENT' || disp.type === 'ADD_SUB')) {
    // Catch all to set record and person ids if a change was made affecting record/person ids. This needs to happen after all create or add sub dispatches.
    setRecordPersonIdsDispatches.push({
      type: 'SET_RECORD_PERSON_IDS',
      value: person.recordId,
    })
  }

  const currentEventIds = new Set([
    ...(currentEvents?.primary?.map((e) => e.id) || []),
    ...(currentEvents?.additional?.map((e) => e.id) || []),
  ])

  Object.keys(eventsById).forEach((eventId) => {
    if (!currentEventIds.has(eventId)) {
      dispatches.push(...handleDeletedEvents(eventsById[eventId]))
    }
  })

  if (!currentEventIds.has(primaryEventId) && !eventsById[primaryEventId]) {
    const deletedEvent = getNestedOrFloatingElementById(images, primaryEventId)
    if (deletedEvent) {
      dispatches.push(...handleDeletedEvents(deletedEvent))
    }
  }

  // Process confirmed unlinks (deferred unlink operations)
  confirmedUnlinkList?.forEach((confirmedUnlink) => {
    dispatches.push(getUnlinkDispatch(confirmedUnlink.event, confirmedUnlink.person))
  })
  confirmedAddLinkList?.forEach((confirmedAddLink) => {
    dispatches.push(getAddLinkDispatch(confirmedAddLink.event, confirmedAddLink.person))

    if (!setRecordPersonIdsDispatches.some((disp) => disp.value === confirmedAddLink.person.recordId)) {
      setRecordPersonIdsDispatches.push({
        type: 'SET_RECORD_PERSON_IDS',
        value: confirmedAddLink.person.recordId,
      })
    }
  })

  dispatches.push(...highlightDispatches)
  dispatches.push(...setRecordPersonIdsDispatches)

  return dispatches
}
